import React, { FC, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import DataTable, { IDataTableHandle } from '@srm/ui/data-table';
import { IDataTableSource, IServerLoadOptions } from '@srm/ui/data-table/server';
import { AddressBookService } from '@services/AddressBookService';
import { IAbookTab } from '@models/Forms/IForms';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import { IAbookSettings } from '@atoms/Abookpicker/AbookpickerModal/IAbookSettings';
import { IAbookpickerRefActions } from '@atoms/Abookpicker/IAbookpickerRefActions';
import { ConfigService } from '@/configuration/services/configService';
import { buildAbookColumns } from './abookGridColumns';

export interface IListModeControlProp extends Omit<IAbookSettings, 'showChips'> {
    currentTab: IAbookTab;
    onSelectedRowItems: (value: IAddressBookData[]) => void;
    onConfirmRow?: (row: IAddressBookData) => void;
    onSelectedKeys: (value: number[]) => void;
    getParams: (options: unknown) => string;
    getSelectedKeys?: (selected: number[]) => Promise<number[]>;
    controlRef: React.Ref<IAbookpickerRefActions>;
}

/**
 * Плоский список адресной книги на базовом гриде.
 *
 * Выборку (страницу, фильтры, сортировку) считает сервер: вкладка задаёт кэш и
 * состав колонок, а параметры запроса собирает модалка (`getParams`) — контракт
 * прежний — его ждёт бэкенд.
 */
const ListModeControl: FC<IListModeControlProp> = (p: IListModeControlProp) => {
    const config = ConfigService.get();
    const applyFilterOnClick = config.application.applyGridFilterOnClick ?? false;
    const tableRef = useRef<IDataTableHandle<IAddressBookData>>(null);
    // Настройки приходят новым объектом на каждый рендер модалки, а загрузчик
    // таблицы обязан быть стабильным: его смена — другая выборка (с начала, выбор снят)
    const settingsRef = useRef(p);
    settingsRef.current = p;
    const [reloadToken, setReloadToken] = useState(0);
    /** Стартовый выбор ставится один раз — после первой порции строк. */
    const initialSelectionRef = useRef(false);

    useImperativeHandle(p.controlRef, () => ({
        reset: () => {
            setReloadToken((token) => token + 1);
        },
        unSelect: (item: IAddressBookData) => {
            const { keys } = tableRef.current?.getSelected() ?? { keys: [] };
            tableRef.current?.selectRows(keys.filter((key) => key !== String(item.key)));
        },
        clearSelection: () => {
            tableRef.current?.clearSelection();
        },
    }));

    /** Отметить уже выбранные записи: их ключи бэкенд отдаёт отдельным запросом. */
    const markInitialSelection = useCallback(async () => {
        const s = settingsRef.current;
        if (initialSelectionRef.current || !s.selected || s.selected.length === 0) return;
        initialSelectionRef.current = true;
        const keys = s.getSelectedKeys ? await s.getSelectedKeys(s.selected) : s.selected;
        tableRef.current?.selectRows(keys.map(String));
    }, []);

    // Вкладка адресной книги задаёт свой кэш на бэкенде: её смена — другая выборка,
    // поэтому загрузчик меняется вместе с ней (таблица начинает выборку с начала).
    const tabCache = p.currentTab.cache;

    const load = useCallback(
        async (options: IServerLoadOptions) => {
            const s = settingsRef.current;
            const response = await AddressBookService.fetchListData(tabCache, s.selected, s.getParams(options));
            markInitialSelection().catch(console.error);
            return { rows: response.data.data ?? [], totalCount: response.data.totalCount ?? 0 };
        },
        [markInitialSelection, tabCache],
    );

    const source = useMemo<IDataTableSource<IAddressBookData>>(
        () => ({ load, refreshToken: reloadToken }),
        [load, reloadToken],
    );

    // Новая вкладка — стартовый выбор ставится заново, когда придут её строки
    const prevTabRef = useRef(tabCache);
    useEffect(() => {
        if (prevTabRef.current === tabCache) return;
        prevTabRef.current = tabCache;
        initialSelectionRef.current = false;
    }, [tabCache]);

    const columns = useMemo(() => buildAbookColumns(p.currentTab.columns), [p.currentTab.columns]);

    /** Данные записей по ключу: выбор может включать строки, которых нет среди
     *  загруженных (стартовый выбор поля до докрутки до его страницы), а сабмит
     *  перезаписывает поле докладом отсюда — терять их нельзя.
     *  Ключ — строка: бэкенд отдаёт `key` строкой (`AbookItemBaseDto.Key`), и такой же
     *  ключ у строки таблицы (`getRowId`) — хранить числом значит ничего не найти. */
    const itemsCacheRef = useRef(new Map<string, IAddressBookData>());
    useEffect(() => {
        itemsCacheRef.current.clear();
    }, [tabCache, reloadToken]);

    const onSelectionChange = useCallback((keys: string[], rows: IAddressBookData[]) => {
        const s = settingsRef.current;
        rows.forEach((row) => itemsCacheRef.current.set(String(row.key), row));
        // ключи строк и есть ключи сотрудников (getRowId), включая ещё
        // не загруженные прокруткой — доклад наружу обязан покрыть их все
        s.onSelectedKeys(keys.map(Number));
        const missing = keys.filter((key) => !itemsCacheRef.current.has(key));
        if (!missing.length) {
            s.onSelectedRowItems(keys.map((key) => itemsCacheRef.current.get(key)!));
            return;
        }
        AddressBookService.fetchAbookItemsByKeys(missing)
            .then((response) => {
                (response.data ?? []).forEach((item) => itemsCacheRef.current.set(String(item.key), item));
                // выбор мог измениться за время запроса — доклад по актуальному
                const current = tableRef.current?.getSelected().keys ?? keys;
                const items = current
                    .map((key) => itemsCacheRef.current.get(key))
                    .filter((item): item is IAddressBookData => !!item);
                settingsRef.current.onSelectedKeys(items.map((item) => Number(item.key)));
                settingsRef.current.onSelectedRowItems(items);
            })
            .catch(console.error);
    }, []);

    return (
        <DataTable<IAddressBookData>
            controlRef={tableRef}
            columns={columns}
            data={[]}
            getRowId={(row) => String(row.key)}
            source={source}
            // список идёт сплошным: строки догружаются прокруткой, а под таблицей
            // висит счётчик «Загружено X из Y»
            serverScrollMode="infinite"
            paging={false}
            selectable
            singleSelection={!p.isMultiple}
            selectOnRowClick
            onSelectionChange={onSelectionChange}
            onRowDoubleClick={p.isMultiple ? undefined : p.onConfirmRow}
            showFilterRow
            applyFiltersOnClick={applyFilterOnClick}
            defaultWrapMode={p.wordWrapEnabled ? 'wrap' : 'nowrap'}
            // высоту задаёт зона .abook-grid — второй предел (maxHeight) дал бы
            // модалке «дышать» по контенту и прыгать после каждого фильтра
            fillHeight
            fitColumns
            overflowTooltip
            emptyText="Нет строк"
        />
    );
};

export default ListModeControl;
